/*
 * Pincode delivery estimate on the product page.
 * Markup: snippets/kor-delivery-estimate.liquid. Docs: docs/ll-architecture.md.
 */
(function (root) {
  'use strict';

  var TIME_ZONE = 'Asia/Kolkata';
  var PINCODE_PATTERN = /^[1-9][0-9]{5}$/;
  var LOOKUP_URL = 'https://api.postalpincode.in/pincode/';
  var LOOKUP_TIMEOUT_MS = 5000;
  var STORAGE_KEY = 'kor:pincode';
  var MESSAGES = {
    invalid: 'Please enter a valid 6-digit pincode.',
    unknown: "We couldn't find this pincode. Please check it and try again."
  };

  // Today's calendar date in India, as a Date at UTC midnight so day maths ignores the viewer's clock zone.
  function todayInIndia(now) {
    var parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: TIME_ZONE,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).formatToParts(now || new Date());
    var get = function (type) {
      return Number(parts.find(function (p) { return p.type === type; }).value);
    };
    return new Date(Date.UTC(get('year'), get('month') - 1, get('day')));
  }

  function addWorkingDays(start, days, saturdayWorking) {
    var date = new Date(start.getTime());
    var added = 0;
    while (added < days) {
      date.setUTCDate(date.getUTCDate() + 1);
      var weekday = date.getUTCDay();
      if (weekday === 0 || (weekday === 6 && !saturdayWorking)) continue;
      added += 1;
    }
    return date;
  }

  function formatShortDate(date) {
    return new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' }).format(date);
  }

  function isValidPincode(value) {
    return PINCODE_PATTERN.test(value);
  }

  // Resolves to { status: 'found', place } | { status: 'unknown' } | { status: 'unavailable' }.
  // 'unavailable' (network error, timeout, odd payload) never blocks the estimate.
  function lookupPincode(pincode, fetchImpl) {
    var doFetch = fetchImpl || root.fetch;
    if (typeof doFetch !== 'function') return Promise.resolve({ status: 'unavailable' });

    var controller = typeof AbortController === 'function' ? new AbortController() : null;
    var timer = controller ? setTimeout(function () { controller.abort(); }, LOOKUP_TIMEOUT_MS) : null;

    return doFetch(LOOKUP_URL + pincode, controller ? { signal: controller.signal } : undefined)
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.json();
      })
      .then(function (data) {
        var entry = Array.isArray(data) ? data[0] : null;
        if (!entry) return { status: 'unavailable' };
        if (entry.Status === 'Error') return { status: 'unknown' };
        var office = entry.Status === 'Success' && entry.PostOffice && entry.PostOffice[0];
        if (!office) return { status: 'unavailable' };
        var place = [office.District, office.State].filter(Boolean).join(', ');
        return place ? { status: 'found', place: place } : { status: 'unavailable' };
      })
      .catch(function () { return { status: 'unavailable' }; })
      .then(function (result) {
        if (timer) clearTimeout(timer);
        return result;
      });
  }

  function buildHeadline(template, makeDays, date) {
    return template.split('{make_days}').join(String(makeDays)).split('{date}').join(formatShortDate(date));
  }

  var api = {
    todayInIndia: todayInIndia,
    addWorkingDays: addWorkingDays,
    formatShortDate: formatShortDate,
    isValidPincode: isValidPincode,
    lookupPincode: lookupPincode,
    buildHeadline: buildHeadline
  };

  if (typeof module === 'object' && module.exports) module.exports = api;
  if (typeof customElements === 'undefined' || customElements.get('kor-delivery-estimate')) return;

  function readStoredPincode() {
    try { return root.localStorage.getItem(STORAGE_KEY) || ''; } catch (e) { return ''; }
  }

  function storePincode(value) {
    try { root.localStorage.setItem(STORAGE_KEY, value); } catch (e) { /* storage blocked: nothing to remember */ }
  }

  class KorDeliveryEstimate extends HTMLElement {
    connectedCallback() {
      if (this.form) return;
      this.form = this.querySelector('form');
      this.input = this.querySelector('input[name="pincode"]');
      this.button = this.querySelector('button[type="submit"]');
      this.result = this.querySelector('[data-result]');
      this.headline = this.querySelector('[data-headline]');
      this.place = this.querySelector('[data-place]');
      this.error = this.querySelector('[data-error]');
      this.requestId = 0;

      this.input.addEventListener('input', this.onInput.bind(this));
      this.form.addEventListener('submit', this.onSubmit.bind(this));

      // Returning visitor: fill in the last pincode and check it quietly, leaving the control usable.
      var stored = readStoredPincode();
      if (isValidPincode(stored)) {
        this.input.value = stored;
        this.check(stored, { background: true });
      }
    }

    onInput() {
      var digits = this.input.value.replace(/\D/g, '').slice(0, 6);
      if (digits !== this.input.value) this.input.value = digits;
      // Any edit makes an in-flight lookup and the shown result stale.
      this.requestId += 1;
      this.setBusy(false);
      this.result.hidden = true;
      this.input.removeAttribute('aria-invalid');
      this.error.hidden = true;
    }

    onSubmit(event) {
      event.preventDefault();
      if (this.getAttribute('aria-busy') === 'true') return;
      var pincode = this.input.value.trim();
      if (!isValidPincode(pincode)) {
        this.showError(MESSAGES.invalid);
        return;
      }
      this.check(pincode);
    }

    check(pincode, options) {
      var requestId = ++this.requestId;
      var background = Boolean(options && options.background);
      if (!background) this.setBusy(true);
      lookupPincode(pincode).then(function (lookup) {
        if (requestId !== this.requestId) return;
        if (!background) this.setBusy(false);
        if (lookup.status === 'unknown') {
          this.showError(MESSAGES.unknown);
          return;
        }
        storePincode(pincode);
        this.showEstimate(lookup.status === 'found' ? lookup.place : '');
      }.bind(this));
    }

    showEstimate(place) {
      var makeDays = Number(this.dataset.makeDays);
      var deliveryDays = Number(this.dataset.deliveryDays);
      var deliveredBy = addWorkingDays(todayInIndia(), deliveryDays, this.dataset.saturdayWorking === 'true');

      this.error.hidden = true;
      this.input.removeAttribute('aria-invalid');
      this.headline.textContent = buildHeadline(this.dataset.resultTemplate, makeDays, deliveredBy);
      this.place.textContent = place ? 'Delivering to ' + place : '';
      this.place.hidden = !place;
      this.result.hidden = false;
    }

    showError(message) {
      this.result.hidden = true;
      this.error.textContent = message;
      this.error.hidden = false;
      this.input.setAttribute('aria-invalid', 'true');
    }

    // aria-disabled, not disabled: a disabled button drops keyboard focus mid-check.
    setBusy(busy) {
      this.button.setAttribute('aria-disabled', String(busy));
      this.setAttribute('aria-busy', String(busy));
    }
  }

  customElements.define('kor-delivery-estimate', KorDeliveryEstimate);
})(typeof window !== 'undefined' ? window : globalThis);
